import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Loader2,
  Package,
  Search,
  Check,
  Heart,
  Upload,
  ArrowLeft,
} from "lucide-react";
import { useBrandProfile } from "../hooks/useBrandProfile";
import { supabase } from "../lib/supabase";
import { fireTnExport, type TnExportProducto } from "../lib/tiendaNube";
import { TiendaNubeIcon } from "../components/TiendaNubeIcon";
import {
  fetchExportableProducts,
  signFullUrl,
  type ExportProductGroup,
} from "./v2/exportApi";
import { HoverScrollCarousel } from "./v2/HoverScrollCarousel";
import { ProductStackCard } from "../components/tnexport/ProductStackCard";
import {
  ProductConfigSheet,
  type ExportAction,
} from "../components/tnexport/ProductConfigSheet";

// Tope de imágenes por exportación (≈ el máximo del import: 50 prod × 3 fotos).
const MAX_EXPORT_IMAGES = 50;

// Fila de un producto (Paso 1): header + tira horizontal de sus generadas, con
// selección ordenada (cada foto elegida se numera 1..N = su posición).
const ProductRow = ({
  group,
  order,
  onToggle,
  onSelectAll,
}: {
  group: ExportProductGroup;
  order: Map<string, number>;
  onToggle: (id: string) => void;
  onSelectAll: () => void;
}) => {
  const allSel = group.images.every((i) => order.has(i.id));
  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <div className="text-[14px] font-medium text-brand-black/80">
          {group.nombre}
        </div>
        <button
          type="button"
          onClick={onSelectAll}
          className="text-[12.5px] font-medium text-brand-primary hover:opacity-70 transition-colors"
        >
          {allSel ? "Quitar todas" : "Seleccionar todas"}
        </button>
      </div>
      <HoverScrollCarousel className="flex gap-3 overflow-x-auto [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden">
        {group.images.map((img) => {
          const pos = order.get(img.id);
          const isSel = pos != null;
          return (
            <button
              key={img.id}
              type="button"
              onClick={() => onToggle(img.id)}
              className={`relative shrink-0 w-32 aspect-square rounded-xl overflow-hidden border-2 transition-colors ${
                isSel
                  ? "border-brand-primary"
                  : "border-transparent hover:border-neutral-200"
              }`}
            >
              {img.thumbUrl ? (
                <img
                  src={img.thumbUrl}
                  alt=""
                  loading="lazy"
                  className="absolute inset-0 w-full h-full object-cover"
                />
              ) : (
                <div className="absolute inset-0 flex items-center justify-center bg-neutral-100">
                  <Package className="w-5 h-5 text-brand-black/25" />
                </div>
              )}
              {isSel && (
                <span className="absolute top-1.5 right-1.5 w-5 h-5 rounded-full bg-brand-primary text-white inline-flex items-center justify-center text-[11px] font-semibold">
                  {pos}
                </span>
              )}
              {img.alreadyExported && (
                <span className="absolute bottom-1 left-1 rounded-full bg-black/60 px-1.5 py-0.5 text-[10px] font-medium text-white">
                  ya exportada
                </span>
              )}
            </button>
          );
        })}
      </HoverScrollCarousel>
    </div>
  );
};

// Wizard "Exportar a Tiendanube":
//  - Paso 1: elegís imágenes por producto (con orden) + toggle de origen.
//  - Paso 2: una card por producto; tap → detalle + acción; Exportar.
export const ExportarTiendanube = () => {
  const { profile, currentBrandId } = useBrandProfile();
  const companyId = profile?.id_company ?? null;

  const [groups, setGroups] = useState<ExportProductGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [onlyFav, setOnlyFav] = useState(false);
  const [origen, setOrigen] = useState<"todos" | "tn" | "atlas">("todos");
  // Selección ordenada: imagen_generada_id -> orden (1..N) dentro de su producto.
  const [order, setOrder] = useState<Map<string, number>>(new Map());
  const [step, setStep] = useState<1 | 2>(1);
  const [openProductId, setOpenProductId] = useState<string | null>(null);
  const [actions, setActions] = useState<Map<string, ExportAction>>(new Map());
  const [exporting, setExporting] = useState(false);
  // Progreso del export (X de N imágenes marcadas por n8n) para feedback en vivo.
  const [exportProgress, setExportProgress] = useState<{
    done: number;
    total: number;
  } | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  // Guard anti-race: la marca (currentBrandId) resuelve async (null → marca), así
  // que el fetch se dispara 2 veces. Ignoramos la respuesta que no sea la última.
  const reqIdRef = useRef(0);

  const load = useCallback(async () => {
    if (!companyId) {
      setLoading(false);
      return;
    }
    const myReq = ++reqIdRef.current;
    setLoading(true);
    const g = await fetchExportableProducts(companyId, currentBrandId);
    if (myReq !== reqIdRef.current) return; // respuesta vieja (cambió la marca) → ignorar
    setGroups(g);
    setLoading(false);
  }, [companyId, currentBrandId]);

  useEffect(() => {
    load();
  }, [load]);

  // Refresco silencioso (para actualizar los badges tras exportar).
  const refresh = useCallback(async () => {
    if (!companyId) return;
    const myReq = ++reqIdRef.current;
    const g = await fetchExportableProducts(companyId, currentBrandId);
    if (myReq !== reqIdRef.current) return;
    setGroups(g);
  }, [companyId, currentBrandId]);

  // Espera hasta que TODOS los ids tengan tn_exported_at (n8n los va marcando a
  // medida que empuja cada foto), o corta a los ~130s. Reporta el avance en cada
  // poll vía `onProgress` para dar feedback en vivo (X/N).
  const waitExported = useCallback(
    async (
      ids: string[],
      onProgress?: (done: number, total: number) => void,
    ): Promise<number> => {
      if (!companyId) return 0;
      // ~2s por imagen (TN descarga la full-res) → damos margen para el tope de 50.
      const deadline = Date.now() + 130000;
      let doneCount = 0;
      while (Date.now() < deadline) {
        const { data } = await supabase
          .from("imagenes_generadas")
          .select("id")
          .in("id", ids)
          .not("tn_exported_at", "is", null);
        doneCount = (data || []).length;
        onProgress?.(doneCount, ids.length);
        if (doneCount >= ids.length) break;
        await new Promise((r) => setTimeout(r, 2500));
      }
      return doneCount;
    },
    [companyId],
  );

  // Renumera las seleccionadas de un producto a 1..N respetando el orden actual.
  const renumber = (m: Map<string, number>, imgIds: string[]) => {
    const sel = imgIds
      .filter((id) => m.has(id))
      .sort((a, b) => m.get(a)! - m.get(b)!);
    sel.forEach((id, i) => m.set(id, i + 1));
  };

  const capReached = () => {
    setToast(`Máximo ${MAX_EXPORT_IMAGES} imágenes por exportación`);
    window.setTimeout(() => setToast(null), 3000);
  };

  const toggleOrdered = (g: ExportProductGroup, id: string) => {
    // Pre-check SOLO para el aviso (puede estar levemente stale). El enforcement
    // real del tope va adentro del updater, con el estado fresco (`prev`).
    if (!order.has(id) && order.size >= MAX_EXPORT_IMAGES) {
      capReached();
      return;
    }
    setOrder((prev) => {
      if (!prev.has(id) && prev.size >= MAX_EXPORT_IMAGES) return prev; // tope (fresco)
      const next = new Map(prev);
      if (next.has(id)) next.delete(id);
      else next.set(id, 999); // se reordena abajo; 999 = "último"
      renumber(
        next,
        g.images.map((i) => i.id),
      );
      return next;
    });
  };

  const selectAllOrdered = (g: ExportProductGroup) => {
    const ids = g.images.map((i) => i.id);
    // Pre-check SOLO para el aviso (puede estar stale; no decide el estado final).
    const willOverflow =
      !ids.every((id) => order.has(id)) &&
      ids.filter((id) => !order.has(id)).length >
        MAX_EXPORT_IMAGES - order.size;
    if (willOverflow) capReached();

    // El estado final se decide con el estado FRESCO (`prev`) dentro del updater.
    setOrder((prev) => {
      const next = new Map(prev);
      if (ids.every((id) => next.has(id))) {
        // Ya estaban todas → deseleccionar.
        ids.forEach((id) => next.delete(id));
      } else {
        // Agregar solo hasta llenar el tope (capacidad calculada del estado fresco).
        const capacity = MAX_EXPORT_IMAGES - next.size;
        ids
          .filter((id) => !next.has(id))
          .slice(0, Math.max(0, capacity))
          .forEach((id, i) => next.set(id, next.get(id) ?? 1000 + i));
      }
      renumber(next, ids);
      return next;
    });
  };

  // Filtro (origen + búsqueda por nombre + favoritos).
  const filtered = useMemo(() => {
    const q = query.toLowerCase().trim();
    return groups
      .filter((g) =>
        origen === "todos"
          ? true
          : origen === "tn"
            ? g.esDeTiendanube
            : !g.esDeTiendanube,
      )
      .map((g) => ({
        ...g,
        images: onlyFav ? g.images.filter((i) => i.favorito) : g.images,
      }))
      .filter((g) => g.images.length > 0)
      .filter((g) => (q ? g.nombre.toLowerCase().includes(q) : true));
  }, [groups, query, onlyFav, origen]);

  // Productos con ≥1 imagen seleccionada, con sus imágenes en orden.
  const productosSeleccionados = useMemo(() => {
    return groups
      .map((g) => {
        const imagenes = g.images
          .filter((i) => order.has(i.id))
          .map((i) => ({
            id: i.id,
            orden: order.get(i.id)!,
            fileName: i.fileName,
            thumbUrl: i.thumbUrl,
          }))
          .sort((a, b) => a.orden - b.orden);
        return { group: g, imagenes };
      })
      .filter((x) => x.imagenes.length > 0);
  }, [groups, order]);

  // Acción por producto. Los de TN arrancan en "add"; los de Atlas en "create"
  // con nombre/precio/descripción prellenados. Editable desde el sheet (tuerca).
  const actionFor = (g: ExportProductGroup): ExportAction => {
    const a = actions.get(g.productoId);
    if (a) return a;
    return g.esDeTiendanube
      ? { kind: "add" }
      : {
          kind: "create",
          nombre: g.nombre,
          precio: g.precioSugerido ?? "",
          descripcion: g.descripcionSugerida ?? "",
          stock: "",
          sku: "",
        };
  };

  // Un producto está "listo" para exportar: los de TN siempre; los de Atlas
  // cuando su form "crear nuevo" tiene nombre + precio.
  const productoListo = (g: ExportProductGroup): boolean => {
    if (g.esDeTiendanube) return true;
    const a = actionFor(g);
    return a.kind === "create" && !!a.nombre.trim() && !!a.precio.trim();
  };
  const todosListos = productosSeleccionados.every((p) =>
    productoListo(p.group),
  );

  const handleExport = async () => {
    if (!companyId || exporting || productosSeleccionados.length === 0) return;
    setExporting(true);
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      const productos: TnExportProducto[] = await Promise.all(
        productosSeleccionados.map(async ({ group, imagenes }) => {
          const a = actionFor(group);
          const imgs = await Promise.all(
            imagenes.map(async (i) => ({
              imagen_generada_id: i.id,
              src: await signFullUrl(companyId, i.fileName),
              orden: i.orden,
            })),
          );
          return {
            producto_id: group.productoId,
            // "send_to_other" = agregar al producto elegido (otro tn_product_id).
            action: a.kind === "send_to_other" ? "add" : a.kind,
            tn_product_id:
              a.kind === "send_to_other" ? a.tnProductId : group.tnProductId,
            ...(a.kind === "create"
              ? {
                  nuevo: {
                    nombre: a.nombre,
                    precio: a.precio,
                    descripcion: a.descripcion,
                    stock: a.stock,
                    sku: a.sku,
                  },
                }
              : {}),
            imagenes: imgs.filter((x) => x.src),
          };
        }),
      );
      await fireTnExport({
        userId: user?.id,
        companyId,
        brandId: currentBrandId,
        productos,
      });

      const allIds = productosSeleccionados.flatMap((p) =>
        p.imagenes.map((i) => i.id),
      );
      setExportProgress({ done: 0, total: allIds.length });
      const done = await waitExported(allIds, (d, total) =>
        setExportProgress({ done: d, total }),
      );
      setOrder(new Map());
      setStep(1);
      await refresh();
      setToast(
        done >= allIds.length
          ? `${allIds.length} ${
              allIds.length === 1 ? "imagen exportada" : "imágenes exportadas"
            } a Tiendanube`
          : `Exportando… ${done}/${allIds.length} listas (puede tardar un toque)`,
      );
      window.setTimeout(() => setToast(null), 4500);
    } finally {
      setExporting(false);
      setExportProgress(null);
    }
  };

  const openSel = openProductId
    ? productosSeleccionados.find((x) => x.group.productoId === openProductId)
    : null;

  return (
    <div className="px-4 sm:px-8 pt-4 pb-24 max-w-content mx-auto">
      <h1 className="text-[17px] font-semibold text-brand-black/90 mb-4 flex items-center gap-2">
        Exportar a Tiendanube
        <TiendaNubeIcon className="h-[18px] w-auto text-[#2c3357]" />
      </h1>

      {step === 1 && (
        <>
          {/* Toggle de origen (segmented, patrón ATLAS) */}
          <div className="mb-4 inline-flex rounded-md border border-neutral-200 bg-surface-card p-0.5">
            {(
              [
                ["todos", "Todos"],
                ["tn", "De Tiendanube"],
                ["atlas", "De Atlas"],
              ] as const
            ).map(([val, label]) => (
              <button
                key={val}
                type="button"
                onClick={() => setOrigen(val)}
                className={`h-8 px-3 rounded-[6px] text-[13px] font-medium transition-colors ${
                  origen === val
                    ? "bg-brand-primary text-white"
                    : "text-neutral-600 hover:bg-neutral-100"
                }`}
              >
                {label}
              </button>
            ))}
          </div>

          {/* Filtros */}
          <div className="mb-5 flex flex-col sm:flex-row sm:items-center gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Buscar producto..."
                className="w-full pl-10 pr-3 h-[var(--btn-h)] rounded-md border border-neutral-200 bg-surface-card text-[length:var(--text-body)] focus:ring-2 focus:ring-brand-primary focus:border-brand-primary outline-none transition-colors"
              />
            </div>
            <button
              type="button"
              onClick={() => setOnlyFav((v) => !v)}
              className={`h-[var(--btn-h)] px-5 inline-flex items-center gap-2 text-[length:var(--text-body)] font-medium border rounded-md shadow-sm transition-colors ${
                onlyFav
                  ? "bg-brand-primary hover:bg-brand-primary-hover text-white border-brand-primary"
                  : "bg-surface-card text-neutral-900 border-neutral-200 hover:bg-neutral-100"
              }`}
            >
              <Heart className={`w-3.5 h-3.5 ${onlyFav ? "fill-current" : ""}`} />
              Favoritos
            </button>
          </div>

          {loading ? (
            <div className="flex justify-center py-16">
              <Loader2 className="w-6 h-6 animate-spin text-brand-black/30" />
            </div>
          ) : filtered.length === 0 ? (
            <div className="flex flex-col items-center justify-center text-center py-16 text-brand-black/40">
              <Package className="w-8 h-8 mb-3" strokeWidth={1.5} />
              <p className="text-[14px]">
                No hay productos con imágenes generadas para este filtro.
              </p>
            </div>
          ) : (
            <div className="space-y-6">
              {filtered.map((g) => (
                <ProductRow
                  key={g.productoId}
                  group={g}
                  order={order}
                  onToggle={(id) => toggleOrdered(g, id)}
                  onSelectAll={() => selectAllOrdered(g)}
                />
              ))}
            </div>
          )}
        </>
      )}

      {step === 2 && (
        <div>
          <button
            type="button"
            onClick={() => setStep(1)}
            className="inline-flex items-center gap-1 text-[13px] text-brand-primary hover:opacity-70 mb-4"
          >
            <ArrowLeft className="w-4 h-4" /> Volver a elegir
          </button>
          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-2.5">
            {productosSeleccionados.map(({ group, imagenes }) => {
              const a = actionFor(group);
              const destino =
                a.kind === "send_to_other" ? a.nombre : undefined;
              return (
                <ProductStackCard
                  key={group.productoId}
                  nombre={group.nombre}
                  thumbs={imagenes.map((i) => i.thumbUrl)}
                  count={imagenes.length}
                  listo={productoListo(group)}
                  destino={destino}
                  onClick={() => setOpenProductId(group.productoId)}
                />
              );
            })}
          </div>

          {openSel && (
            <ProductConfigSheet
              group={openSel.group}
              brandId={currentBrandId}
              imagenes={openSel.imagenes.map((i) => ({
                id: i.id,
                orden: i.orden,
                thumbUrl: i.thumbUrl,
              }))}
              action={actionFor(openSel.group)}
              onChangeAction={(a) =>
                setActions((m) =>
                  new Map(m).set(openSel.group.productoId, a),
                )
              }
              onReorder={(ids) =>
                setOrder((prev) => {
                  const next = new Map(prev);
                  ids.forEach((id, i) => next.set(id, i + 1));
                  return next;
                })
              }
              onRemove={(id) =>
                setOrder((prev) => {
                  const next = new Map(prev);
                  next.delete(id);
                  return next;
                })
              }
              onClose={() => setOpenProductId(null)}
            />
          )}
        </div>
      )}

      {/* CTA fija (fondo sólido, sin blur) */}
      {step === 1 && productosSeleccionados.length > 0 && (
        <div className="fixed inset-x-0 bottom-0 z-20 bg-surface-card border-t border-black/[0.08] px-4 sm:px-8 py-3 flex items-center justify-between">
          <span className="text-[12.5px] text-neutral-500">
            {order.size}/{MAX_EXPORT_IMAGES} imágenes
          </span>
          <button
            type="button"
            onClick={() => setStep(2)}
            className="h-[var(--btn-h)] px-6 inline-flex items-center gap-2 bg-brand-primary hover:bg-brand-primary-hover text-white rounded-md shadow-sm text-[length:var(--text-body)] font-medium transition-colors"
          >
            Continuar ({productosSeleccionados.length})
          </button>
        </div>
      )}

      {step === 2 && productosSeleccionados.length > 0 && (
        <div className="fixed inset-x-0 bottom-0 z-20 bg-surface-card border-t border-black/[0.08] px-4 sm:px-8 py-3 flex items-center justify-end gap-3">
          {!todosListos && (
            <span className="text-[12.5px] text-neutral-500">
              Completá nombre y precio en los productos de Atlas.
            </span>
          )}
          <button
            type="button"
            onClick={handleExport}
            disabled={exporting || !todosListos}
            className="h-[var(--btn-h)] px-6 inline-flex items-center gap-2 bg-brand-primary hover:bg-brand-primary-hover text-white rounded-md shadow-sm text-[length:var(--text-body)] font-medium transition-colors disabled:opacity-70"
          >
            {exporting ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                {exportProgress && exportProgress.total > 0
                  ? `Exportando… ${exportProgress.done}/${exportProgress.total}`
                  : "Exportando…"}
              </>
            ) : (
              <>
                <Upload className="w-4 h-4" />
                Exportar
              </>
            )}
          </button>
        </div>
      )}

      {/* Toast de confirmación (no bloqueante, se va solo) */}
      {toast && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-30 inline-flex items-center gap-2 bg-neutral-900 text-white pl-2.5 pr-4 py-2.5 rounded-lg shadow-lg animate-modal-appear">
          <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-emerald-500 shrink-0">
            <Check className="w-3 h-3" />
          </span>
          <TiendaNubeIcon className="h-4 w-auto text-white shrink-0" />
          <span className="text-[13.5px] font-medium">{toast}</span>
        </div>
      )}
    </div>
  );
};

export default ExportarTiendanube;
